import { useState, useEffect } from 'react';
import {
  Resources,
  ListParams,
  PositionTemplateFieldType,
} from '@erecruit/types';
import { getResourceApi } from '@erecruit/modules';
import {
  singleSelectFields,
  multiSelectFields,
  datepickerFields,
  getDataSourceForField,
} from '../../../RequestPosition/state/helpers';
import { FormFieldType, PositionField, ResourceFilter } from '../../types';

export interface DynamicFieldSections {
  general: PositionTemplateFieldType[];
  location: PositionTemplateFieldType[];
  schedule: PositionTemplateFieldType[];
  review: PositionTemplateFieldType[];
  aboutPosition: PositionTemplateFieldType[];
  billing: PositionTemplateFieldType[];
  additionalInfo: PositionTemplateFieldType[];
}

export const dynamicFields: DynamicFieldSections = {
  general: [
    PositionTemplateFieldType.Agreement,
    PositionTemplateFieldType.MaxNumberOfSubmittals,
    PositionTemplateFieldType.Opportunity,
    PositionTemplateFieldType.PrimaryDepartment,
    PositionTemplateFieldType.ReportsTo,
    PositionTemplateFieldType.SubmittalLimitPerVendor,
    PositionTemplateFieldType.Supervisor,
    PositionTemplateFieldType.TravelPercentage,
    PositionTemplateFieldType.ClientProject,
    PositionTemplateFieldType.FolderGroup,
  ],
  schedule: [
    PositionTemplateFieldType.Duration,
    PositionTemplateFieldType.StartTime,
    PositionTemplateFieldType.EndTime,
    PositionTemplateFieldType.EstimatedStartDate,
    PositionTemplateFieldType.EstimatedEndDate,
    PositionTemplateFieldType.ShiftTypes,
  ],
  location: [
    PositionTemplateFieldType.WorksiteAddress,
    PositionTemplateFieldType.FacilityDepartments,
    PositionTemplateFieldType.WorkFromHome,
  ],
  review: [
    PositionTemplateFieldType.PositionCreationReason,
    PositionTemplateFieldType.PositionCreationReasonNote,
    PositionTemplateFieldType.GeneralComments,
  ],
  aboutPosition: [
    PositionTemplateFieldType.NumberOfPositions,
    PositionTemplateFieldType.PositionType,
    PositionTemplateFieldType.PositionTitle,
    PositionTemplateFieldType.PositionDescription,
    PositionTemplateFieldType.ClientProject,
    PositionTemplateFieldType.FolderGroup,
    PositionTemplateFieldType.Industry,
    PositionTemplateFieldType.PrimaryDepartment,
    PositionTemplateFieldType.Supervisor,
    PositionTemplateFieldType.WorkHours,
  ],
  billing: [PositionTemplateFieldType.RequestFundingDetails],
  additionalInfo: [],
};

export const getFormFieldType = (fieldType: PositionTemplateFieldType) => {
  if (multiSelectFields.indexOf(fieldType) !== -1) {
    return FormFieldType.MultiSelect;
  }

  if (singleSelectFields.indexOf(fieldType) !== -1) {
    return FormFieldType.SingleSelect;
  }

  if (datepickerFields.indexOf(fieldType) !== -1) {
    return FormFieldType.Date;
  }

  return FormFieldType.Text;
};

export const generateFolderGroupId = (
  folderGroupId: Resources.FolderGroupKey,
) => {
  return `${folderGroupId.id}${
    folderGroupId.aliasId ? '@' + folderGroupId.aliasId : ''
  }`;
};

export const degenerateFolderGroupId = (id: string) => {
  const ids = id.split('@');
  const folderGroupId: Resources.FolderGroupKey = {
    id: Number(ids[0]),
    aliasId: Number(ids[1]),
  };
  return folderGroupId;
};

export async function fetchData<T>(
  field: PositionField,
  searchTerm?: string,
  ids: number[] = [],
): Promise<T[]> {
  const dataSource = getDataSourceForField(field);
  const params: Partial<ListParams<ResourceFilter>> = {
    filter: {
      searchTerm,
      ids,
    },
    pager: {
      offset: 0,
      limit: 10,
    },
  };

  dataSource.resourceParams = params;

  if (field.fieldType === PositionTemplateFieldType.FolderGroup) {
    const paramsIds = ids.filter(id => !isNaN(id));
    dataSource.resourceParams = {
      filter: {
        searchTerm,
        ids: paramsIds.map(
          id =>
            (({
              id,
              aliasId: null,
            } as unknown) as Resources.FolderGroupKey),
        ),
      },
      pager: {
        offset: 0,
        limit: 10,
      },
    } as Partial<ListParams<Resources.FolderGroupFilter>>;
  }

  const responses = await getResourceApi().getResources([dataSource]);

  if (field.fieldType === PositionTemplateFieldType.FolderGroup) {
    return responses[0].data.map((folderGroup: Resources.FolderGroup) => ({
      id: generateFolderGroupId({
        id: folderGroup.key.id,
        aliasId: folderGroup.key.aliasId,
      }),
      name: folderGroup.name,
    }));
  }

  return responses[0].data as T[];
}

export const useWorksite = (
  worksiteAddressField: PositionField,
  worksiteId?: number,
) => {
  const [worksite, setWorksite] = useState<Resources.Worksite>();

  useEffect(() => {
    if ((worksiteId || worksiteAddressField.value) && !worksite) {
      fetchData<Resources.Worksite>(worksiteAddressField, undefined, [
        worksiteId || worksiteAddressField.value,
      ]).then(worksites => {
        setWorksite(worksites[0]);
      });
    }
  }, [worksite, worksiteAddressField, worksiteId]);

  return { worksite };
};
